import { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";

export const useKeyboardShortcuts = (setHelpOpen) => {
  const navigate = useNavigate();
  const gPrefixRef = useRef(false);
  const prefixTimeoutRef = useRef(null);

  useEffect(() => {
    const handleKeyDown = (e) => {
      const hasModifiers = e.ctrlKey || e.altKey || e.metaKey;

      // Modifier actions (Bypass typing guard so they work inside input fields)
      // Alt+Shift+N -> New sale
      if (e.altKey && e.shiftKey && e.key.toLowerCase() === "n") {
        e.preventDefault();
        navigate("/sales/salesPos");
        return;
      }

      // Alt+Shift+P -> New purchase
      if (e.altKey && e.shiftKey && e.key.toLowerCase() === "p") {
        e.preventDefault();
        navigate("/purchase/purchasebill");
        return;
      }

      // Alt+Shift+C -> New salesb2c
      if (e.altKey && e.shiftKey && e.key.toLowerCase() === "c") {
        e.preventDefault();
        navigate("/sales/salesPosb2c");
        return;
      }

      // Alt+H -> Open Item History
      if (e.altKey && e.key.toLowerCase() === "h") {
        e.preventDefault();
        const event = new CustomEvent("openItemHistory");
        window.dispatchEvent(event);
        return;
      }

      // Alt+P -> Open Item Stock Details
      if (e.altKey && e.key.toLowerCase() === "p") {
        e.preventDefault();
        const event = new CustomEvent("openItemStockModal");
        window.dispatchEvent(event);
        return;
      }

      // 1. Typing guard (CRITICAL)
      // Bail out if the event target is an input, textarea, select, or contenteditable
      let target = e.target;
      if (
        target.tagName === "INPUT" ||
        target.tagName === "TEXTAREA" ||
        target.tagName === "SELECT" ||
        target.isContentEditable
      ) {
        return;
      }

      // Traverse parentElement to catch focus inside contenteditable children
      while (target) {
        if (target.isContentEditable) return;
        target = target.parentElement;
      }

      // Help overlay toggle
      if (e.key === "?" && !hasModifiers) {
        e.preventDefault();
        setHelpOpen((prev) => !prev);
        return;
      }

      // G-prefix state machine
      if (!hasModifiers) {
        if (e.key.toLowerCase() === "g") {
          e.preventDefault();

          // Re-pressing G restarts the timer
          gPrefixRef.current = true;
          if (prefixTimeoutRef.current) {
            clearTimeout(prefixTimeoutRef.current);
          }

          prefixTimeoutRef.current = setTimeout(() => {
            gPrefixRef.current = false;
          }, 1200);
          return;
        }

        // If G prefix is active, check for the second key
        if (gPrefixRef.current) {
          const key = e.key.toLowerCase();
          let matched = false;

          switch (key) {
            case "h":
              navigate("/");
              matched = true;
              break;
            case "d":
              navigate("/dashboard");
              matched = true;
              break;
            case "n":
              navigate("/product");
              matched = true;
              break;
            case "r":
              navigate("/accounts/receipt");
              matched = true;
              break;
            case "p":
              navigate("/accounts/payment");
              matched = true;
              break;
            case "w":
              navigate("/others/warehouseTransfer");
              matched = true;
              break;
            default:
              break;
          }

          if (matched) {
            e.preventDefault();
            // Reset state
            gPrefixRef.current = false;
            if (prefixTimeoutRef.current) {
              clearTimeout(prefixTimeoutRef.current);
            }
          }
        }
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
      if (prefixTimeoutRef.current) {
        clearTimeout(prefixTimeoutRef.current);
      }
    };
  }, [navigate, setHelpOpen]);
};
